import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  Injectable,
  Injector,
  Type,
} from '@angular/core';
import { ModalComponent } from './modal.component';
import { ModalRef } from './modal-ref';

export interface ModalConfig<T> {
  component: Type<T>;
  data?: Partial<T>;
  width?: string;
  maxWidth?: string;
  disableBackdropClose?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ModalService {
  private openCount = 0;
  private modalStack: ModalRef[] = [];

  constructor(
    private appRef: ApplicationRef,
    private injector: Injector,
    private envInjector: EnvironmentInjector
  ) {}

  open<T, R = unknown>(config: ModalConfig<T>): ModalRef<R> {
    const modalRef = new ModalRef<R>();

    const modalInjector = Injector.create({
      providers: [{ provide: ModalRef, useValue: modalRef }],
      parent: this.injector,
    });

    const modalComponentRef: ComponentRef<ModalComponent> = createComponent(
      ModalComponent,
      {
        environmentInjector: this.envInjector,
        elementInjector: modalInjector,
      }
    );

    const instance = modalComponentRef.instance;
    instance.contentComponent = config.component;
    instance.contentInjector = modalInjector;
    instance.contentData = (config.data as Record<string, unknown>) ?? {};

    if (config.width) {
      instance.width = config.width;
    }
    if (config.maxWidth) {
      instance.maxWidth = config.maxWidth;
    }
    if (config.disableBackdropClose != null) {
      instance.disableBackdropClose = config.disableBackdropClose;
    }

    this.openCount++;
    this.modalStack.push(modalRef);
    document.body.style.overflow = 'hidden';

    const onEscape = (e: KeyboardEvent) => {
      if (
        e.key === 'Escape' &&
        !config.disableBackdropClose &&
        this.modalStack[this.modalStack.length - 1] === modalRef
      ) {
        modalRef.close();
      }
    };
    document.addEventListener('keydown', onEscape);

    this.appRef.attachView(modalComponentRef.hostView);

    const domElem = (modalComponentRef.hostView as any).rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);

    modalRef.afterClosed().subscribe(() => {
      document.removeEventListener('keydown', onEscape);
      this.modalStack = this.modalStack.filter((ref) => ref !== modalRef);
      this.openCount--;
      if (this.openCount === 0) {
        document.body.style.overflow = '';
      }
      this.appRef.detachView(modalComponentRef.hostView);
      modalComponentRef.destroy();
      domElem.remove();
    });

    return modalRef;
  }
}
